import { Edit, InfoOutline } from '@mui/icons-material';
import {
  Box,
  Breakpoint,
  IconButton,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { Column, Row } from '../Layout';

type StaticFieldSize = 'sm' | 'md' | 'lg';

const sizesMap: Record<StaticFieldSize, string> = {
  sm: '220px',
  md: '400px',
  lg: '500px',
};

const labelSizesMap: Record<StaticFieldSize, string> = {
  sm: '210px',
  md: '290px',
  lg: '320px',
};

const EditButton = styled(IconButton)({ padding: 0 });
const EditIcon = styled(Edit)({
  width: 16,
  height: 16,
});

const ColumnWrapper = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StaticFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const RowWrapper = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: StaticFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
  alignItems: 'flex-start',
}));

const LabelColumn = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'labelSize' && prop !== 'isHorizontal',
})<{ labelSize?: StaticFieldSize; isHorizontal?: boolean }>(
  ({ labelSize, isHorizontal }) => ({
    flex:
      isHorizontal && labelSize
        ? `0 0 ${labelSizesMap[labelSize]}`
        : '0 0 auto',
  }),
);

const ChildrenWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHorizontal',
})<{ isHorizontal?: boolean }>(({ theme, isHorizontal }) => ({
  width: '100%',
  ...theme.typography.h6,
  ...(isHorizontal && { flex: 1 }),
}));

type Props = {
  label?: string | ReactNode;
  tooltip?: string;
  size?: StaticFieldSize;
  spacing?: keyof Theme['spacingSizes'];
  editable?: boolean;
  onEdit?: () => void;
  layout?: 'horizontal' | 'vertical';
  labelSize?: StaticFieldSize;
  breakpoint?: Breakpoint;
};

const StaticField: FC<PropsWithChildren<Props>> = ({
  children,
  label,
  size,
  tooltip,
  spacing = 'xs',
  editable = false,
  onEdit,
  layout = 'vertical',
  labelSize,
  breakpoint,
}) => {
  const theme = useTheme();
  const isCollapsed =
    useMediaQuery(theme.breakpoints.down(breakpoint ?? 'xs')) && !!breakpoint;
  const isHorizontal = layout === 'horizontal' && !isCollapsed;
  const Wrapper = isHorizontal ? RowWrapper : ColumnWrapper;

  return (
    <Wrapper size={isHorizontal ? undefined : size} spacing={spacing}>
      {!!label &&
        (typeof label === 'string' ? (
          <LabelColumn
            spacing="xs"
            labelSize={labelSize}
            isHorizontal={isHorizontal}
          >
            <Row centered spacing="xxs">
              <Typography color="grey.600" variant="h6">
                {label}
              </Typography>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              ) : null}
              {editable ? (
                <EditButton size="small" onClick={onEdit}>
                  <EditIcon />
                </EditButton>
              ) : null}
            </Row>
          </LabelColumn>
        ) : (
          label
        ))}

      <ChildrenWrapper isHorizontal={isHorizontal}>{children}</ChildrenWrapper>
    </Wrapper>
  );
};

export default StaticField;
